import React from "react";
import {
  CpSelectSingle,
  CpSelectSingleProps,
} from "../cp-select-single.component";
import {
  useController,
  FieldValues,
  Control,
  UseControllerProps,
  FieldPath,
  FieldPathValue,
  RegisterOptions,
} from "react-hook-form";

export type CpSelectSingleFieldProps<
  TData,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
  CpSelectSingleProps<TData, NonNullable<FieldPathValue<TFieldValues, TName>>>,
  "value" | "onChange" | "onBlur" | "error"
> & {
  fieldName: TName;
  control: Control<TFieldValues>;
  controllerOptions?: Omit<
    UseControllerProps<TFieldValues, TName>,
    "name" | "control" | "rules"
  >;
  required?: boolean;
  rules?: RegisterOptions<TFieldValues, TName>;
};

export function CpSelectSingleField<
  TData,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: CpSelectSingleFieldProps<TData, TFieldValues, TName>) {
  const {
    fieldName,
    control,
    controllerOptions,
    required,
    rules = {},
    data,
    transformData,
    ...cpSelectSingleProps
  } = props;

  const mergedRules: RegisterOptions<TFieldValues, TName> = {
    ...rules,
  };
  if (required) {
    mergedRules.required = "Field is required";
  }

  const {
    field: { value, onBlur, onChange, ref },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
    rules: mergedRules,
    ...controllerOptions,
  });

  return (
    <CpSelectSingle<TData, NonNullable<FieldPathValue<TFieldValues, TName>>>
      {...cpSelectSingleProps}
      data={data}
      transformData={transformData}
      error={error?.message}
      value={value}
      onBlur={onBlur}
      onChange={onChange}
      triggerRef={ref as any}
    />
  );
}
